/**
 * FlowHub - Auth Layout Component
 * Modern animated layout for login and register pages
 */

import { ReactNode } from 'react';
import { Typography } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface AuthLayoutProps {
  children: ReactNode;
  title: string;
  subtitle: string;
  features?: string[];
  stats?: Array<{ label: string; value: string }>;
}

function AuthLayout({ children, title, subtitle, features = [], stats = [] }: AuthLayoutProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-pink-50 to-orange-50">
      <div className="grid min-h-screen lg:grid-cols-2">
        {/* Form column - Animated entrance */}
        <div className="flex items-center justify-center px-6 py-12 sm:px-10 lg:px-16 xl:px-24 bg-white/80 backdrop-blur-xl animate-slideInLeft">
          <div className="w-full max-w-md">
            {/* Logo */}
            <div className="mb-10">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-violet-600 to-pink-600 flex items-center justify-center shadow-lg shadow-violet-500/50 animate-float">
                  <span className="text-xl font-bold text-white">FH</span>
                </div>
                <div>
                  <Title level={3} className="!m-0 !text-slate-900">
                    FlowHub
                  </Title>
                  <Text className="text-violet-600 font-medium">Automação inteligente com IA</Text>
                </div>
              </div>
              <div className="mt-8">
                <Title level={2} className="!mb-3 !text-slate-900 animate-fadeIn">
                  {title}
                </Title>
                <Text type="secondary" className="text-base">
                  {subtitle}
                </Text>
              </div>
            </div>

            {/* Form content */}
            <div className="animate-fadeInUp">
              {children}
            </div>

            {/* Features */}
            {features.length > 0 && (
              <div className="mt-10 space-y-3 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircleOutlined className="text-violet-600" />
                    {feature}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Illustration column - Animated background */}
        <div className="relative hidden overflow-hidden lg:block">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-violet-600 via-pink-600 to-orange-500" />
          
          {/* Animated geometric shapes */}
          <div className="absolute inset-0">
            {/* Large circle top right */}
            <div className="absolute -top-20 -right-20 w-96 h-96 bg-pink-500/30 rounded-full blur-3xl animate-pulse-slow" />
            
            {/* Medium circle bottom left */}
            <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-orange-500/30 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
            
            {/* Small floating circles */}
            <div className="absolute top-1/4 right-1/4 w-32 h-32 bg-white/10 rounded-full animate-float" style={{ animationDelay: '0.5s' }} />
            <div className="absolute bottom-1/3 right-1/3 w-24 h-24 bg-white/10 rounded-full animate-float" style={{ animationDelay: '1.5s' }} />
            <div className="absolute top-1/2 left-1/4 w-20 h-20 bg-white/10 rounded-full animate-float" style={{ animationDelay: '2s' }} />
            
            {/* Abstract shapes */}
            <div className="absolute top-1/3 right-20 w-40 h-40 bg-white/5 rounded-3xl rotate-45 animate-spin-slow" />
            <div className="absolute bottom-1/4 left-20 w-32 h-32 bg-white/5 rounded-full animate-bounce-slow" />
          </div>

          {/* Content overlay */}
          <div className="relative flex h-full flex-col justify-between px-14 py-12 text-white z-10">
            <div className="animate-fadeInUp">
              <span className="inline-flex items-center rounded-full bg-white/20 backdrop-blur-md px-4 py-2 text-sm font-medium shadow-lg">
                ✨ Plataforma all-in-one
              </span>
              <h2 className="mt-8 text-5xl font-bold leading-tight drop-shadow-2xl">
                Agentes de IA
                <br />
                <span className="text-orange-200">sem programar</span>
              </h2>
              <p className="mt-6 max-w-lg text-lg text-white/90 drop-shadow-lg leading-relaxed">
                Conecte canais, sincronize dados e acompanhe resultados em tempo real com dashboards construídos para equipes de atendimento e vendas.
              </p>
            </div>

            {/* Stats */}
            {stats.length > 0 && (
              <div className="space-y-4 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
                <div className="grid gap-4 sm:grid-cols-2">
                  {stats.map((stat, index) => (
                    <div key={index} className="rounded-2xl bg-white/10 backdrop-blur-md p-5 shadow-xl border border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-105">
                      <p className="text-xs uppercase tracking-wider text-white/70 font-semibold">{stat.label}</p>
                      <p className="mt-2 text-3xl font-bold drop-shadow-lg">{stat.value}</p>
                    </div>
                  ))}
                </div>
                <div className="rounded-2xl bg-white/10 backdrop-blur-md p-5 shadow-xl border border-white/20">
                  <p className="text-sm font-medium text-white/90">🔗 Integrações prontas:</p>
                  <p className="mt-2 text-xl font-bold">WhatsApp • Google Calendar • N8N</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthLayout;
